/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import UiFilterLabel from './Filter/UiFilterLabel'
import { MagnifyingGlassIcon } from '@heroicons/react/24/solid'
import { useDebouncedCallback } from 'use-debounce'
import TableFilterRemoveButton
    from 'components/Ui/Tanstack/Filters/components/TableFilterRemoveButton'

type SearchInputField = {
    search: string
}

interface UiSearchProps {
    label?: string
    placeholder?: string
    onChange?: (value: string) => void
    value?: string
    onSubmit?: (data: SearchInputField) => void
    disabled?: boolean
    inputId?: string
    fullWidth?: boolean
}

const UiSearch = (props: UiSearchProps) => {
    const [internalValue, setInternalValue] = useState(
        props.value ? props.value : ''
    )
    const { t } = useTranslation()
    
    const debouncedChangeHandler = useDebouncedCallback((data) => {
        props.onChange && props.onChange(data)
    }, 300)

    const onSubmitHandler = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        console.log('onSubmitHandler', { search: internalValue })
        if (props.onSubmit) {
            props.onSubmit({ search: internalValue })
        }
    }

    const onChangeHandler = (data: string) => {
        setInternalValue(data)
        if (props.onChange) {
            debouncedChangeHandler(data)
        }
    }

    const removeFiltersHandler = () => {
        setInternalValue('')
        if (props.onChange) {
            props.onChange('')
        }
        if (props.onSubmit) {
            props.onSubmit({ search: '' })
        }
    }

    useEffect(() => {
        setInternalValue(props.value ? props.value : '')
    }, [props.value])

    return (
        <div
            className={clsx(
                'UiSearch flex flex-col',
                props.fullWidth ? 'w-full' : 'w-full md:w-80'
            )}
        >
            {props.label && (
                <div className={clsx('relative flex flex-row items-center')}>
                    <UiFilterLabel label={props.label} />
                    <TableFilterRemoveButton
                        className={clsx('relative! right-auto! mb-2')}
                        inputValue={internalValue}
                        onClick={removeFiltersHandler}
                    />
                </div>
            )}
            <div className={clsx('flex flex-row items-center')}>
                <form
                    className={clsx(
                        'SearchItem flex flex-row items-center justify-start max-w-md w-full relative bg-white shadow-md border border-gray-300 rounded-lg transition-all duration-200 hover:-translate-y-0.5 focus-within:ring-2 focus-within:ring-blue-400 dark:bg-gray-900 dark:border-gray-700'
                    )}
                    onSubmit={onSubmitHandler}
                >
                    <input
                        className={clsx(
                            'w-full inline-flex items-center pl-3.5 pr-16 py-2 text-sm font-semibold transition duration-150 bg-transparent placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-0 focus:border-0 border-0',
                            props.value
                                ? 'text-blue-500'
                                : 'text-gray-700 dark:text-gray-100',
                            props?.label ? 'pr-16' : 'pr-18'
                        )}
                        disabled={props.disabled}
                        name={'search'}
                        id={props.inputId}
                        placeholder={
                            props.placeholder ?? t('Type search string here...')
                        }
                        type="text"
                        value={internalValue}
                        onChange={(e) => onChangeHandler(e.target.value)}
                    />
                    {!props?.label && (
                        <TableFilterRemoveButton
                            className={clsx('right-11! border-r pr-1.5 z-10')}
                            inputValue={internalValue}
                            onClick={removeFiltersHandler}
                        />
                    )}
                    <button
                        className={clsx(
                            'absolute right-0 inset-y-0 px-3.5 flex items-center rounded-tr-lg rounded-br-lg transition-all duration-200 transform',
                            'bg-gray-100 dark:bg-gray-800 text-gray-500 hover:bg-blue-400 hover:text-white dark:hover:bg-blue-500'
                        )}
                        disabled={props.disabled}
                        type={'submit'}
                    >
                        <MagnifyingGlassIcon
                            className={clsx('w-4 h-4 z-10 relative')}
                        />
                    </button>
                </form>
            </div>
        </div>
    )
}

export default UiSearch
